//@ts-check
import apiClient from './apiClient';
/** @type {import('./types').LookupCaching} */

var CACHE = {};
var INVALIDATED_FIELDS_CACHE = {};
/**
 * @param {Array<string>} fields
 * @param {Array<{Name:string, Value:string}>} parameters
 * @param {number} startIndex
 * @param {number} maxRowsStep
 * @param {string} context
 * @returns {string}
 */

var createCacheKey = function createCacheKey(fields, parameters, startIndex, maxRowsStep, context) {
  var key = '';
  fields && fields.forEach(function (f) {
    return key += f;
  });
  parameters && parameters.forEach(function (p) {
    return key += p.Name + p.Value;
  });
  key += startIndex;
  key += maxRowsStep;
  key += context;
  return btoa(key);
};
/**
 * @param {string} workspace
 * @param {string} dataSource
 * @param {string} xpath
 * @param {string} cacheKey
 * @param {Array<{Name:string, Value:string, Id: string}>} parameters
 * @returns {import('./types').lookupApiResult}
 */


var clearCachedDataIfInvalidatedParameters = function clearCachedDataIfInvalidatedParameters(workspace, dataSource, xpath, cacheKey, parameters) {
  var invalidated = false;
  parameters.forEach(function (param) {
    if (param.Id && INVALIDATED_FIELDS_CACHE[param.Id]) {
      invalidated = true; // cache is invalidated

      delete INVALIDATED_FIELDS_CACHE[param.Id]; // remove invalidated field from cache
    }
  });
  if (!invalidated) return;
  if (!CACHE[workspace]) return;
  if (!CACHE[workspace][dataSource]) return;
  if (!CACHE[workspace][dataSource][xpath]) return;
  delete CACHE[workspace][dataSource][xpath][cacheKey];
};
/**
 * @param {string} workspace
 * @param {string} dataSource
 * @param {string} xpath
 * @param {string} cacheKey
 * @returns {import('./types').lookupApiResult}
 */


var getCachedData = function getCachedData(workspace, dataSource, xpath, cacheKey) {
  if (!CACHE[workspace]) return;
  if (!CACHE[workspace][dataSource]) return;
  if (!CACHE[workspace][dataSource][xpath]) return;
  return CACHE[workspace][dataSource][xpath][cacheKey];
};
/**
 * @param {string} workspace
 * @param {string} dataSource
 * @param {string} xpath
 * @param {string} cacheKey
 * @param {string} filterFieldName
 * @param {import('./types').lookupApiResult} result
 */


var cacheResult = function cacheResult(workspace, dataSource, xpath, cacheKey, filterFieldName, result) {
  if (filterFieldName) return;
  CACHE[workspace] = CACHE[workspace] || {};
  CACHE[workspace][dataSource] = CACHE[workspace][dataSource] || {};
  CACHE[workspace][dataSource][xpath] = CACHE[workspace][dataSource][xpath] || {};
  CACHE[workspace][dataSource][xpath][cacheKey] = result;
};
/**
 * @param {import('./types').lookupApiResult} result
 * @param {number} maxRowsStep
 * @param {string} filterValue
 * @returns {boolean}
 */


var canUseCachedResult = function canUseCachedResult(result, maxRowsStep, filterValue) {
  if (!result || !result.rows) return false; // we can use this cached result since no filter is required

  if (!filterValue) return true; // if filter is required and the server contains more data than cached data
  // we need to fetch new data and apply filter on server

  if (result.rows.length === maxRowsStep) return false;
  return true;
};
/**
 *
 * @param {import('./types').lookupApiResult} result
 * @param {string} filterFieldName
 * @param {string} filterValue
 * @param {boolean} matchExact
 * @returns {import('./types').lookupApiResult}
 */


var filterCachedResult = function filterCachedResult(result, filterFieldName, filterValue, matchExact) {
  if (!filterValue) return result;
  var columnIndex = result.columnNames.indexOf(filterFieldName);
  return {
    columnNames: result.columnNames,
    rows: result.rows.filter(function (row) {
      return isMatching(row, columnIndex, filterValue, matchExact);
    })
  };
};
/**
 *
 * @param {import('./types').lookupApiRowResult} row
 * @param {number} columnIdx
 * @param {string} filterValue
 * @param {boolean} matchExact
 * @return {boolean}
 */


var isMatching = function isMatching(row, columnIdx, filterValue, matchExact) {
  filterValue = filterValue.toLowerCase();

  if (columnIdx >= 0) {
    var columnValue = (row.values[columnIdx] || '').toLowerCase();
    return matchExact ? columnValue === filterValue : columnValue.indexOf(filterValue) !== -1;
  }

  return !!row.values.find(function (value) {
    if (!value) return false;
    var columnValue = value.toLowerCase();
    return matchExact ? columnValue === filterValue : columnValue.indexOf(filterValue) !== -1;
  });
};
/**
 * Perform a new lookup into a specified datasource for a collection of nodes
 * @param {string} workspace
 * @param {string} dataSource
 * @param {string} xpath
 * @param {Array<string>} fields
 * @param {Array<{Name:string, Value:string, Id:string}>} parameters
 * @param {import('./types').LookupFilter} filter
 * @returns {Promise<import('./types').lookupApiResult>}
 */


var lookup = function lookup(workspace, dataSource, xpath, fields, parameters) {
  var filter = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
  var startIndex = filter && filter.startRowIdx || 0;
  var maxRowsStep = filter && filter.maxRowsStep || 100;
  var context = filter && filter.context || '';
  var matchExact = filter && filter.matchExact || false;
  var filterFieldName = filter && filter.filterFieldName || '';
  var filterValue = filter && filter.filterValue || '';
  var cacheKey = createCacheKey(fields, parameters, startIndex, maxRowsStep, context); // try to clear cache if some parameters are invalidated

  clearCachedDataIfInvalidatedParameters(workspace, dataSource, xpath, cacheKey, parameters);
  var result = getCachedData(workspace, dataSource, xpath, cacheKey);
  if (canUseCachedResult(result, maxRowsStep, filterValue)) return Promise.resolve(filterCachedResult(result, filterFieldName, filterValue, matchExact));
  return apiClient.lookup(workspace, dataSource, xpath, fields, parameters, filter).then(function (result) {
    cacheResult(workspace, dataSource, xpath, cacheKey, filterFieldName, result);
    return result;
  });
};

export var invalidateFieldBindingCache = function invalidateFieldBindingCache(fieldId) {
  INVALIDATED_FIELDS_CACHE[fieldId] = true;
};
export default lookup;